/** 
 * @namespace MediaGallery
 * @memberof Promega.Components
 * @description Methods for controlling media gallery components. A Media Gallery component consists of a set of thumbnails and a display area for the main image or video.
 * 
 * @example <caption>A Media Gallery component has the class <code>media-gallery</code> and contains a thumbnail tray and main item. The contents of the item container
 * (<code>&lt;div&gt;</code> with class <code>media-gallery__item-container</code>) are replaced whenever the user selects an item from the thumbnail tray.</caption>
 * <div class="media-gallery">
 *   <div class="media-gallery__thumbnail-tray">
 *     <div class="media-gallery__thumbnail-wrapper media-gallery__thumbnail-wrapper--selected" data-media-type="image" data-media-src="path-to-full-resolution-src.jpg" data-media-caption="" data-media-width="1249" data-media-height="752">
 *       <img src="path-to-thumbnail-resolution-src.jpg" alt="Alt text for the image" class="media-gallery__thumbnail" width="320">
 *     </div>
 *     <div class="media-gallery__thumbnail-wrapper media-gallery__thumbnail-wrapper--selected" data-media-type="image" data-media-src="path-to-full-resolution-src2.jpg" data-media-caption="" data-media-width="1249" data-media-height="752">
 *       <img src="path-to-thumbnail-resolution-src2.jpg" alt="Alt text for the image" class="media-gallery__thumbnail" width="320">
 *     </div>
 *   </div>
 *   <div class="media-gallery__item-container">
 *     <img class="media-gallery__item" alt="Alt text for the image" src="path-to-full-resolution-src.jpg" width="1249" height="752">
 *   </div>
 * </div>
 * 
 * @example <caption>The Media Gallery component supports both images and videos. Set attribute <code>data-media-type</code> to <code>video</code>, and
 * specify either <code>inline</code> or <code>modal</code> as the value of attribute <code>data-playback-behavior</code> to assign it inline
 * or modal behavior. Also make sure to include a thumbnail in attribute <code>data-video-poster</code>.</caption>
 * <div class="media-gallery">
 *   <div class="media-gallery__thumbnail-tray">
 *     <div class="media-gallery__thumbnail-wrapper" data-media-type="video" data-media-src="path-to-video.mp4" data-video-poster="path-to-poster-image.jpg" data-playback-behavior="inline">
 *       <img src="path-to-thumbnail.jpg" alt="Alt text for the thumbnail" class="media-gallery__thumbnail">
 *     </div>
 *   </div>
 *   <div class="media-gallery__item-container">
 *     <img class="media-gallery__item" alt="Alt text for the image" src="path-to-full-resolution-src.jpg" width="1249" height="752">
 *   </div>
 * </div>
 */

import trackEvent from '../util/trackEvent';

const GALLERY_CLASS = "media-gallery",
    THUMBNAIL_TRAY_CLASS = "media-gallery__thumbnail-tray",
    THUMBNAIL_CLASS = "media-gallery__thumbnail",
    WRAPPER_CLASS = "media-gallery__thumbnail-wrapper",
    WRAPPER_SELECTED_CLASS = "media-gallery__thumbnail-wrapper--selected",
    ITEM_CONTAINER_CLASS = "media-gallery__item-container",
    ITEM_CLASS = "media-gallery__item",
    GALLERY_CAPTION_CLASS = "media-gallery__caption",
    CAPTION_CLASS = "caption-text";
    
// Private functions/listeners

function thumbnailClicked (event) {
    const eventTarget = event.target.closest(`.${WRAPPER_CLASS}`);

    if (eventTarget) {
        const galleryParent = eventTarget.closest(`.${GALLERY_CLASS}`),
            trayParent = galleryParent.getElementsByClassName(THUMBNAIL_TRAY_CLASS)[0],
            thumbIndex = Array.prototype.indexOf.call(trayParent.children, eventTarget);
        showMedia(galleryParent, thumbIndex);

        const eventSource = event.target.closest(".media-gallery--product-purchase") ? "Purchasing section" : "Page component";
        trackEvent("Components", "Media Gallery - switched images", eventSource);
    }
}

function getIndexOfGallery (galleryParent) {
    const allGalleries = document.getElementsByClassName(GALLERY_CLASS);
    return Array.prototype.indexOf.call(allGalleries, galleryParent);
}

// API

/**
 * @function showMedia
 * @memberof Promega.Components.MediaGallery
 * @description Given a media gallery component, selects the item at the provided index.
 * @param {HTMLElement} galleryParent An element representing the entire media gallery component
 * @param {Number} itemIndex The index of the item to select
 */
export function showMedia (galleryParent, itemIndex) {
    if (galleryParent && itemIndex > -1 && itemIndex !== getCurrentlySelectedIndex(galleryParent)) {
        // Get the needed elements
        const itemEl = galleryParent.getElementsByClassName(ITEM_CLASS)[0],
            captionEl = galleryParent.getElementsByClassName(GALLERY_CAPTION_CLASS)[0],
            itemContainerEl = galleryParent.getElementsByClassName(ITEM_CONTAINER_CLASS)[0],
            thumbnailTrayEl = galleryParent.getElementsByClassName(THUMBNAIL_TRAY_CLASS)[0],
            thumbnailWrapperEl = thumbnailTrayEl.children[itemIndex],
            thumbnailEl = thumbnailWrapperEl.getElementsByClassName(THUMBNAIL_CLASS)[0],
            containerBoundingClientRect = itemContainerEl.getBoundingClientRect(),
            containerWidth = containerBoundingClientRect.width;

        // Get the media data
        const mediaType = thumbnailWrapperEl.getAttribute("data-media-type"),
            mediaSrc = thumbnailWrapperEl.getAttribute("data-media-src"),
            mediaSrcset = thumbnailWrapperEl.getAttribute("data-media-srcset"),
            mediaWidth = thumbnailWrapperEl.getAttribute("data-media-width"),
            mediaHeight = thumbnailWrapperEl.getAttribute("data-media-height"),
            mediaAlt = thumbnailEl.getAttribute("alt");

        // Remove the old item and caption
        itemEl.parentNode.removeChild(itemEl);
        if (captionEl) captionEl.parentNode.removeChild(captionEl);

        if (mediaType === "image") {
            const newEl = document.createElement("img"),
                loadKey = window.performance && typeof window.performance.now === "function" ? performance.now() : Date.now() + Math.random();
                        
            newEl.className = `${ITEM_CLASS} lazyload`;
            newEl.setAttribute("data-gallery-index", getIndexOfGallery(galleryParent));
            newEl.setAttribute("data-load-key", loadKey);
            newEl.alt = mediaAlt;
            newEl.setAttribute("data-src", mediaSrc);
            if (mediaSrcset) newEl.setAttribute("data-srcset", mediaSrcset);
            if (mediaWidth) newEl.width = mediaWidth;
            else newEl.width = containerWidth;
            if (mediaHeight) newEl.height = mediaHeight;

            itemContainerEl.appendChild(newEl);
        }

        else if (mediaType === "video") {
            const newEl = document.createElement("div"),
                mediaPoster = thumbnailWrapperEl.getAttribute("data-video-poster"),
                mediaBehavior = thumbnailWrapperEl.getAttribute("data-playback-behavior");
                    
            newEl.className = ITEM_CLASS;

            const videoContainerEl = document.createElement("div");
            videoContainerEl.className = "content-item__video-wrapper";
            videoContainerEl.setAttribute("data-video-src", mediaSrc);
            videoContainerEl.setAttribute("data-video-poster", mediaPoster);
            videoContainerEl.setAttribute("data-video-title", mediaAlt);
            if (mediaWidth) videoContainerEl.setAttribute("data-video-width", mediaWidth);
            if (mediaHeight) videoContainerEl.setAttribute("data-video-height", mediaHeight);
                    
            if (mediaBehavior === "inline") videoContainerEl.className += " trigger--inline-video";
            else videoContainerEl.className += " trigger--modal-video";

            const videoPosterEl = document.createElement("img");
            videoPosterEl.alt = mediaAlt;
            videoPosterEl.src = mediaPoster;
            if (mediaWidth) videoPosterEl.width = mediaWidth;
            if (mediaHeight) videoPosterEl.height = mediaHeight;

            videoContainerEl.appendChild(videoPosterEl);
            newEl.appendChild(videoContainerEl);
            itemContainerEl.appendChild(newEl);
        }

        else throw `Invalid media type "${mediaType}" clicked in media gallery.`;

        // Check if there is a caption for the new image
        const newCaptionText = thumbnailWrapperEl.getAttribute("data-media-caption");
        if (newCaptionText) {
            const newCaptionEl = document.createElement("div");
            newCaptionEl.innerHTML = newCaptionText;
            newCaptionEl.className = `${GALLERY_CAPTION_CLASS} ${CAPTION_CLASS}`;
            itemContainerEl.appendChild(newCaptionEl);
        }

        // Update the thumbnails
        const currentSelection = thumbnailTrayEl.getElementsByClassName(WRAPPER_SELECTED_CLASS)[0];
        if (currentSelection) currentSelection.classList.remove(WRAPPER_SELECTED_CLASS);
        thumbnailWrapperEl.classList.add(WRAPPER_SELECTED_CLASS);
    }
}

/**
 * @function getCurrentlySelectedIndex
 * @memberof Promega.Components.MediaGallery
 * @description Given a media gallery component, returns the index of the item that is currently selected.
 * @param {HTMLElement} galleryParent An element representing the entire media gallery component
 * @returns {Number} The zero-based index of the item that is currently selected
 */
export function getCurrentlySelectedIndex (galleryParent) {
    if (galleryParent) {
        let child = galleryParent.getElementsByClassName(WRAPPER_SELECTED_CLASS)[0],
            index = 0;
        while ((child = child.previousElementSibling) != null) index++;
        return index;
    }
}

// Init code

{
    document.addEventListener("click", thumbnailClicked);
}
